/**
 * AudioWorklet processor sources.
 *
 * These run on the audio render thread and are shipped as **strings** so the
 * bundle stays a single file: {@link ensureWorklet} turns each source into a
 * Blob URL and registers it via `audioContext.audioWorklet.addModule(...)`.
 * The globals used here (`AudioWorkletProcessor`, `registerProcessor`,
 * `sampleRate`) exist only inside the worklet scope, which is why this is a
 * plain JS string and not a typed module.
 */

/** Processor name registered by {@link MIC_CAPTURE_WORKLET}. */
export const MIC_CAPTURE_PROCESSOR = "mic-capture-processor";
/** Processor name registered by {@link PCM_PLAYBACK_WORKLET}. */
export const PCM_PLAYBACK_PROCESSOR = "pcm-playback-processor";

/**
 * Capture processor: accumulates mono input into fixed-size chunks (at the
 * context sample rate) and posts each chunk plus its RMS level to the main
 * thread. Resampling to the target rate happens on the main thread.
 */
export const MIC_CAPTURE_WORKLET = `
class MicCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = (options && options.processorOptions) || {};
    this._chunkSamples = opts.chunkSamples || 2048;
    this._buffer = new Float32Array(this._chunkSamples);
    this._offset = 0;
  }
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const channel = input[0];
    if (!channel) return true;
    for (let i = 0; i < channel.length; i++) {
      this._buffer[this._offset++] = channel[i];
      if (this._offset >= this._chunkSamples) {
        const frame = this._buffer.slice(0, this._chunkSamples);
        let sumSq = 0;
        for (let j = 0; j < frame.length; j++) sumSq += frame[j] * frame[j];
        const level = Math.sqrt(sumSq / frame.length);
        this.port.postMessage({ type: 'frame', frame, level }, [frame.buffer]);
        this._offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('${MIC_CAPTURE_PROCESSOR}', MicCaptureProcessor);
`;

/**
 * Playback processor: pulls queued Float32 chunks (already at the context rate)
 * into the output, supports `clear` (barge-in / interruption), and posts a
 * periodic output level plus a `drained` event when the queue empties.
 */
export const PCM_PLAYBACK_WORKLET = `
class PcmPlaybackProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._queue = [];
    this._head = null;
    this._headPos = 0;
    this._draining = true;
    this._levelCounter = 0;
    this.port.onmessage = (e) => {
      const d = e.data;
      if (d.type === 'chunk') {
        this._queue.push(d.chunk);
      } else if (d.type === 'clear') {
        this._queue.length = 0;
        this._head = null;
        this._headPos = 0;
      }
    };
  }
  process(_inputs, outputs) {
    const output = outputs[0];
    if (!output || output.length === 0) return true;
    const channel = output[0];
    if (!channel) return true;
    let sumSq = 0;
    for (let i = 0; i < channel.length; i++) {
      if (!this._head || this._headPos >= this._head.length) {
        this._head = this._queue.shift() || null;
        this._headPos = 0;
      }
      const s = this._head ? this._head[this._headPos++] : 0;
      channel[i] = s;
      sumSq += s * s;
    }
    for (let c = 1; c < output.length; c++) output[c].set(channel);
    const empty = !this._head && this._queue.length === 0;
    if (empty && !this._draining) {
      this._draining = true;
      this.port.postMessage({ type: 'drained' });
    } else if (!empty) {
      this._draining = false;
    }
    this._levelCounter += channel.length;
    if (this._levelCounter >= 1024) {
      this._levelCounter = 0;
      this.port.postMessage({ type: 'level', level: Math.sqrt(sumSq / channel.length) });
    }
    return true;
  }
}
registerProcessor('${PCM_PLAYBACK_PROCESSOR}', PcmPlaybackProcessor);
`;
